import { useEffect, useMemo, useState } from 'react'
import { InputGroupKeys } from 'store/customer/enums'
import { ZeroOrPositiveNumbersWithNoDecimals } from 'constants/regex'
import { useTranslation } from 'next-i18next/pages'
import { RegisterOptions, useFormContext } from 'react-hook-form'
import { MIN_USAGE_FOR_USUAL } from 'features/simulation/Simulation.constants'
import { Banner, Checkbox, Form, Icon, InlineMessage, TextField, Tooltip } from '@boltenergy-be/design-system'
import { checkHasInjectionUsageFields, checkHasSolarKva } from 'features/simulation/utils'
import { UsageStepFormInputs } from 'features/simulation/steps/types'
import { CustomUsageFormProps } from 'features/usage-form/types'
import { convertAmountOfSolarPanelsToKva, convertKvaToInjection } from 'utils/conversion'
import { useStoreSelector } from 'hooks/store'
import { MAX_SOLAR_KVA } from 'constants/usages'
import UsageInputLabelComponent from 'features/usage-form/custom-usage-form/usage-input-label-component/UsageInputLabelComponent'
import CheckboxList from 'components/checkbox-list/CheckboxList'
import { selectIsBoltDriveFlexFlow } from 'features/bolt-drive-flex/selectors'
import { COUNTER_TYPES, METER_TYPES, REGIONS } from '@boltenergy-be/shared'

const CustomUsageForm = ({ onBlur, needsGas }: CustomUsageFormProps) => {
  // Redux
  const { inputs } = useStoreSelector((store) => store.customer)
  const marketingStore = useStoreSelector((store) => store.marketing)
  const { region } = inputs[InputGroupKeys.SIMULATION]
  const isBoltDriveFlexFlow = selectIsBoltDriveFlexFlow(marketingStore)

  // i18n
  const { t } = useTranslation(['simulation', 'common', 'validation', 'registration'])

  // React Hook Form
  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid }
  } = useFormContext<UsageStepFormInputs>()
  const watchCounterType = watch('counterType')
  const watchElectricity = watch('electricity')
  const watchGas = watch('gas')
  const watchHasExclNight = watch('hasExclNight')
  const watchHasSolarPanels = watch('hasSolarPanels')
  const watchMeterType = watch('meterType')
  const watchSolarPanels = watch('solarPanels')
  const watchWalloniaSolarPanelsSince2024 = watch('walloniaSolarPanelsSince2024')

  // Local state
  const [showSolarPanels, setShowSolarPanels] = useState<boolean>(!!watchSolarPanels)

  // Constants
  const isSingleMeter = watchMeterType === METER_TYPES.single
  const isBru = region === REGIONS.brussels
  const isAnalog = watchCounterType === COUNTER_TYPES.analog
  const hasInjectionUsageFields = useMemo<boolean>(
    () =>
      checkHasInjectionUsageFields({
        counterType: watchCounterType,
        hasSolarPanels: watchHasSolarPanels
      }),
    [watchCounterType, watchHasSolarPanels]
  )
  const hasSolarKva = useMemo<boolean>(
    () =>
      checkHasSolarKva({
        counterType: watchCounterType,
        hasSolarPanels: watchHasSolarPanels,
        walloniaSolarPanelsSince2024: watchWalloniaSolarPanelsSince2024
      }),
    [watchCounterType, watchHasSolarPanels, watchWalloniaSolarPanelsSince2024]
  )

  /**
   * Saves the solar panels input in the store
   *
   * @param {number | null} solarPanels
   */
  const saveSolarPanelsInput = (solarPanels: number | null) => {
    setValue('solarPanels', solarPanels || undefined)

    // Calculate solar power (in kva) based on given solar panels
    const solarPowerKva = convertAmountOfSolarPanelsToKva(solarPanels)
    setValue('solarKva', Math.round(solarPowerKva * 100) / 100, { shouldValidate: true })

    // Calculate injection
    const injection = convertKvaToInjection(solarPowerKva)

    // Set single meter injection
    setValue('electricity.injection.single', Math.round(injection), { shouldValidate: true })

    const solarPowerKwhDay = injection * 0.66
    setValue('electricity.injection.double.day', Math.round(solarPowerKwhDay), { shouldValidate: true })
    setValue('electricity.injection.double.night', Math.round(injection - solarPowerKwhDay), { shouldValidate: true })
  }

  /**
   * Returns the validation object for a given label
   *
   * @param {string} label
   * @param {boolean} required
   * @returns {RegisterOptions<UsageStepFormInputs>}
   */
  const getValidationObject = (label: string, required: boolean = true): RegisterOptions<UsageStepFormInputs> => {
    return {
      min: { value: 0, message: t('validation:min.number', { attribute: label, min: 0 }) },
      pattern: { value: ZeroOrPositiveNumbersWithNoDecimals, message: t('validation:noDecimals', { attribute: label }) },
      ...(required && {
        required: t('validation:required')
      })
    }
  }

  /**
   * Checks if the usage is unusual
   *
   * @param {number} [usage]
   * @returns {boolean}
   */
  const isUnusualUsage = (usage?: number): boolean => {
    return !(watchHasSolarPanels && isAnalog) && usage > 0 && usage < MIN_USAGE_FOR_USUAL
  }

  useEffect(() => {
    if (isBoltDriveFlexFlow && watchCounterType !== COUNTER_TYPES.digital) {
      setValue('counterType', COUNTER_TYPES.digital)
    }
  }, [isBoltDriveFlexFlow, watchCounterType])

  return (
    <Form onBlur={onBlur && isValid ? () => onBlur(getValues()) : undefined}>
      {isBoltDriveFlexFlow && <Banner type="informative" as="section" title={t('common:driveFlexWarnings.digitalMeter')} />}

      <CheckboxList>
        <Checkbox
          checked={watchMeterType === METER_TYPES.double}
          id="meter"
          label={t('simulation:steps.usage.form.hasDoubleRate')}
          name="options"
          onChange={() => setValue('meterType', watchMeterType === METER_TYPES.double ? METER_TYPES.single : METER_TYPES.double)}
        />

        <Checkbox
          checked={watchCounterType === COUNTER_TYPES.digital}
          disabled={(region === REGIONS.wallonia && watchWalloniaSolarPanelsSince2024) || isBoltDriveFlexFlow}
          id="counterType"
          label={t('simulation:steps.usage.form.hasDigital')}
          name="options"
          onChange={() => {
            if (isBru && watchCounterType === COUNTER_TYPES.digital) {
              setValue('hasSolarPanels', false)
            } else if (region === REGIONS.wallonia && watchCounterType === COUNTER_TYPES.digital) {
              setValue('walloniaSolarPanelsSince2024', false)
            }

            setValue('counterType', watchCounterType === COUNTER_TYPES.digital ? COUNTER_TYPES.analog : COUNTER_TYPES.digital)
          }}
        />

        <Checkbox
          checked={watchHasExclNight}
          id="hasExclNight"
          label={t('simulation:steps.usage.form.hasExclNight')}
          name="options"
          onChange={() => setValue('hasExclNight', !watchHasExclNight)}
        />

        <Checkbox
          checked={watchHasSolarPanels}
          disabled={isBru && isAnalog}
          id="hasSolarPanels"
          label={
            <span>
              {t('simulation:steps.usage.form.hasSolarPanels')}{' '}
              {isBru && isAnalog && (
                <Tooltip tooltipContent={t('simulation:steps.usage.form.noSolarPanelsBruAnalog')}>
                  <Icon name="questionMarkCircle" />
                </Tooltip>
              )}
            </span>
          }
          name="options"
          onChange={() => setValue('hasSolarPanels', !watchHasSolarPanels)}
        />

        {region === REGIONS.wallonia && watchHasSolarPanels && (
          <Checkbox
            checked={watchWalloniaSolarPanelsSince2024}
            id="walloniaSolarPanelsSince2024"
            label={t('common:walloniaSolarPanelsSince2024')}
            name="options"
            onChange={() => {
              setValue('walloniaSolarPanelsSince2024', !watchWalloniaSolarPanelsSince2024)
              if (!watchWalloniaSolarPanelsSince2024) {
                setValue('counterType', COUNTER_TYPES.digital)
              }
            }}
          />
        )}
      </CheckboxList>

      {/* ELECTRICITY CONSUMPTION */}

      {isSingleMeter ? (
        // SINGLE RATE
        <>
          <TextField
            {...register('electricity.consumption.single', getValidationObject(t('simulation:steps.usage.form.electricity')))}
            error={errors?.electricity?.consumption?.single?.message}
            grid="full"
            label={t('simulation:steps.usage.form.electricity')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
          />
          {isUnusualUsage(watchElectricity?.consumption?.single) && (
            <InlineMessage className="grid-col-full" type="warning">
              {t('simulation:steps.usage.alerts.unusualUsage')}
            </InlineMessage>
          )}
        </>
      ) : (
        // DOUBLE RATE
        <>
          <TextField
            {...register('electricity.consumption.double.day', getValidationObject(t('simulation:steps.usage.form.dayMeter')))}
            error={errors?.electricity?.consumption?.double?.day?.message}
            grid="full"
            label={t('simulation:steps.usage.form.dayMeter')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
          />
          {isUnusualUsage(watchElectricity?.consumption?.double?.day) && (
            <InlineMessage className="grid-col-full" type="warning">
              {t('simulation:steps.usage.alerts.unusualUsage')}
            </InlineMessage>
          )}

          <TextField
            {...register('electricity.consumption.double.night', getValidationObject(t('simulation:steps.usage.form.nightMeter')))}
            error={errors?.electricity?.consumption?.double?.night?.message}
            grid="full"
            label={t('simulation:steps.usage.form.nightMeter')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
          />
          {isUnusualUsage(watchElectricity?.consumption?.double?.night) && (
            <InlineMessage className="grid-col-full" type="warning">
              {t('simulation:steps.usage.alerts.unusualUsage')}
            </InlineMessage>
          )}
        </>
      )}

      {watchHasExclNight && (
        <>
          <TextField
            {...register('electricity.consumption.exclusiveNight', getValidationObject(t('simulation:steps.usage.form.exclNight')))}
            error={errors?.electricity?.consumption?.exclusiveNight?.message}
            grid="full"
            label={t('simulation:steps.usage.form.exclNight')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
          />
          {isUnusualUsage(watchElectricity?.consumption?.exclusiveNight) && (
            <InlineMessage className="grid-col-full" type="warning">
              {t('simulation:steps.usage.alerts.unusualUsage')}
            </InlineMessage>
          )}
        </>
      )}

      {/* ELECTRICITY INJECTION */}

      {watchHasSolarPanels && (
        <>
          {hasSolarKva && !showSolarPanels && (
            // SOLAR KVA
            <TextField
              {...register('solarKva', {
                max: {
                  value: MAX_SOLAR_KVA,
                  message: t('validation:max.number', { attribute: t('simulation:steps.usage.form.solarKva'), max: MAX_SOLAR_KVA })
                },
                min: { value: 0, message: t('validation:min.number', { attribute: t('simulation:steps.usage.form.solarKva'), min: 0 }) },
                required: t('validation:required')
              })}
              error={errors?.solarKva?.message}
              grid="full"
              label={t('simulation:steps.usage.form.solarKva')}
              labelComponentSlot={
                <UsageInputLabelComponent
                  label={t('common:dontKnow')}
                  onClick={() => setShowSolarPanels(true)}
                  tooltip={t('simulation:steps.usage.form.solarKvaInfo')}
                />
              }
              type="number"
              step={0.01}
              suffix={{
                as: 'kVA'
              }}
            />
          )}

          {hasInjectionUsageFields && !showSolarPanels && (
            // INJECTION FIELDS
            <>
              {isSingleMeter ? (
                <TextField
                  {...register('electricity.injection.single', getValidationObject(t('simulation:steps.usage.form.injection')))}
                  error={errors?.electricity?.injection?.single?.message}
                  grid="full"
                  label={t('simulation:steps.usage.form.injection')}
                  labelComponentSlot={<UsageInputLabelComponent label={t('common:dontKnow')} onClick={() => setShowSolarPanels(true)} />}
                  type="number"
                  step={1}
                  suffix={{
                    as: 'kWh'
                  }}
                />
              ) : (
                <>
                  <TextField
                    {...register('electricity.injection.double.day', getValidationObject(t('simulation:steps.usage.form.injectionDay')))}
                    error={errors?.electricity?.injection?.double?.day?.message}
                    grid="full"
                    label={t('simulation:steps.usage.form.injectionDay')}
                    labelComponentSlot={<UsageInputLabelComponent label={t('common:dontKnow')} onClick={() => setShowSolarPanels(true)} />}
                    type="number"
                    step={1}
                    suffix={{
                      as: 'kWh'
                    }}
                  />

                  <TextField
                    {...register(
                      'electricity.injection.double.night',
                      getValidationObject(t('simulation:steps.usage.form.injectionNight'))
                    )}
                    error={errors?.electricity?.injection?.double?.night?.message}
                    grid="full"
                    label={t('simulation:steps.usage.form.injectionNight')}
                    labelComponentSlot={<UsageInputLabelComponent label={t('common:dontKnow')} onClick={() => setShowSolarPanels(true)} />}
                    type="number"
                    step={1}
                    suffix={{
                      as: 'kWh'
                    }}
                  />
                </>
              )}
            </>
          )}

          {(hasSolarKva || hasInjectionUsageFields) && showSolarPanels && (
            <TextField
              {...register('solarPanels', getValidationObject(t('simulation:steps.usage.form.injectionNight')))}
              error={errors?.solarPanels?.message}
              grid="full"
              label={t('simulation:steps.usage.form.solarPanels')}
              labelComponentSlot={
                <UsageInputLabelComponent
                  label={
                    hasInjectionUsageFields ? t('simulation:steps.usage.form.injectionKnown') : t('simulation:steps.usage.form.kvaKnown')
                  }
                  onClick={() => setShowSolarPanels(false)}
                />
              }
              onChange={(e) => saveSolarPanelsInput(e.target.value ? Number(e.target.value) : null)}
              type="number"
              step={1}
            />
          )}
        </>
      )}

      {/* GAS */}

      {needsGas && (
        <>
          <TextField
            {...register('gas', getValidationObject(t('simulation:steps.usage.form.gas'), needsGas))}
            error={errors?.gas?.message}
            grid="full"
            label={t('simulation:steps.usage.form.gas')}
            type="number"
            step={1}
            suffix={{
              as: 'kWh'
            }}
          />
          {!!watchGas && watchGas > 0 && watchGas < MIN_USAGE_FOR_USUAL && (
            <InlineMessage className="grid-col-full" type="warning">
              {t('simulation:steps.usage.alerts.unusualUsage')}
            </InlineMessage>
          )}
        </>
      )}
    </Form>
  )
}

export default CustomUsageForm
